import { dataLayerConfig } from '@aspendental/shared-components-web';
import axios from 'axios';

import { LocationResult } from '../../types/SchedulerTypes';

import { typeMapping } from '@src/constants';
import { devError, devLog } from '@src/utils/environment';

// Interface for appointment types response
interface AppointmentTypesResponse {
	[key: string]: string;
}

// Interface for appointment types utilities parameters
interface AppointmentTypesParams {
	facilityDetails?: LocationResult | null;
	training?: boolean;
	routerQuery?: any;
	setStatus: (status: 'loading' | 'default' | 'valid' | 'invalid') => void;
	setAccountId: (accountId: string) => void;
	setAppointmentTypes: (types: AppointmentTypesResponse) => void;
	setValidTypes: (types: any[]) => void;
	setLocation: (location: string) => void;
	setWebsiteFacility: (siteId: string, siteName: string) => void;
	setBrand: (brand: string) => void;
	validateTags: (availableKeys: string[]) => any[];
	setSession?: (sessionData: { schedJWT: string; ssId: string }) => void;
	onAccountIdError?: () => void;
}

/**
 * Fetches appointment types using facility details from context (when lat/lon are not available)
 */
export const getAppointmentTypesByFacility = async (
	facilityDetails: LocationResult,
	siteId: string,
	params: AppointmentTypesParams
): Promise<void> => {
	const {
		setStatus,
		setAccountId,
		setAppointmentTypes,
		setValidTypes,
		setLocation,
		setWebsiteFacility,
		setBrand,
		validateTags,
		onAccountIdError,
	} = params;

	setStatus('loading');

	try {
		const locationAccountId = facilityDetails.revspringAccountId;

		if (!locationAccountId) {
			setStatus('invalid');
			if (onAccountIdError) {
				onAccountIdError();
			}
			return;
		}

		setAccountId(locationAccountId);

		const response = await axios.get(
			`${process.env.APP_PATH}/api/apptTypes/${facilityDetails?.revspringLocationValue}-${locationAccountId}/`
		);

		devLog('Appointment types loaded successfully from facility context');
		setStatus('valid');
		setAppointmentTypes(response.data);
		const availableKeys = Object.keys(response.data);
		setValidTypes(validateTags(availableKeys));
		setLocation(facilityDetails.siteName);
		setWebsiteFacility(facilityDetails.siteId, facilityDetails.siteName);
		setBrand(facilityDetails.currentBrand || 'WellNow');
	} catch (error: any) {
		setStatus('invalid');
		devError('Error fetching appointment types from facility context:', error);
		dataLayerConfig({
			event: 'error',
			step_name: 'appointment_flow_error',
			error_details: error?.response?.data?.error || 'Unknown error',
			error_text: 'An error occurred while fetching appointment types.',
			error_type: 'API error - facility context',
			error_status: error?.response?.status === 403 ? 'Yes' : 'No',
		});
	}
};

/**
 * Fetches appointment types using coordinates from locations endpoint
 */
export const getAppointmentTypesByCoordinates = async (
	latitude: string,
	longitude: string,
	params: AppointmentTypesParams
): Promise<void> => {
	const {
		facilityDetails,
		training,
		routerQuery,
		setStatus,
		setAccountId,
		setAppointmentTypes,
		setValidTypes,
		setLocation,
		setWebsiteFacility,
		setBrand,
		validateTags,
		onAccountIdError,
	} = params;

	setStatus('loading');

	try {
		// 1. Get locations from locations endpoint (for lat/lon based search)
		const locationDetail = await axios.get(
			`${process.env.APP_PATH}/api/locations/${latitude}/${longitude}/0/${training ? 'Y' : 'N'}/`
		);

		if (!locationDetail.data || locationDetail.data.length === 0) {
			throw new Error('No locations found');
		}

		// 2. Get location based on facility code from URL or default
		const locationQuery = locationDetail.data.find((location: LocationResult) => location.code === routerQuery?.fc);
		const location = locationQuery || locationDetail.data[0];

		// Use facility details from context if available
		if (facilityDetails) {
			location.detailedInfo = [facilityDetails];
		}

		if (!location) {
			throw new Error('Location not found for the specified facility code');
		}

		devLog('Selected location:', location.code, location.siteName);

		// 3. Get accountId for this location - prioritize facility context provider
		const locationAccountId = facilityDetails?.revspringAccountId;
		const locationCode = location.code;

		if (!locationAccountId) {
			setStatus('invalid');
			if (onAccountIdError) {
				onAccountIdError();
			}
			return;
		}

		setAccountId(locationAccountId);

		// 4. Authenticate and create a session if needed
		const { authenticate } = await import('@src/components/auth/authUtils');
		const authResult = await authenticate();

		if (!authResult?.orgJWT) {
			throw new Error('Authentication failed: No orgJWT returned');
		}

		// 5. Check if we already have a valid session for this location
		const { SessionManager } = await import('@src/utils/sessionManager');
		const existingSession = SessionManager.getSession(locationCode);

		let sessionResponse;

		if (existingSession && existingSession.ssId && existingSession.schedJWT) {
			devLog('Using existing session for location:', locationCode, 'with ssId:', existingSession.ssId);
			sessionResponse = {
				ssId: existingSession.ssId,
				schedJWT: existingSession.schedJWT,
			};
		} else {
			// 6. If there's no valid session, create a new one specific for this location
			const { createLocationSession } = await import('@src/components/auth/authUtils');
			sessionResponse = await createLocationSession(locationCode);

			// Verify that session creation was successful
			if (!sessionResponse?.schedJWT || !sessionResponse?.ssId) {
				// If session creation fails, try once more with forced authentication
				const newAuthResult = await authenticate();
				if (!newAuthResult?.orgJWT) {
					throw new Error('Authentication retry failed');
				}

				sessionResponse = await createLocationSession(locationCode);
				if (!sessionResponse?.schedJWT || !sessionResponse?.ssId) {
					throw new Error('Session creation failed after retry');
				}
			}

			devLog('Created new session for location:', locationCode, 'with ssId:', sessionResponse.ssId);
		}

		// 7. Get appointment types
		devLog('Fetching appointment types with session:', sessionResponse.ssId);
		const response = await axios.get(
			`${process.env.APP_PATH}/api/apptTypes/${facilityDetails?.revspringLocationValue}-${locationAccountId}/`
		);

		devLog('Appointment types loaded successfully');
		setStatus('valid');
		setAppointmentTypes(response.data);
		const availableKeys = Object.keys(response.data);
		setValidTypes(validateTags(availableKeys));
		setLocation(location.siteName);
		setWebsiteFacility(location.siteId, location.siteName);
		setBrand(location.currentBrand || 'WellNow');
	} catch (error: any) {
		setStatus('invalid');
		devError('Error fetching appointment types:', error);
		dataLayerConfig({
			event: 'error',
			step_name: 'appointment_flow_error',
			error_details: error?.response?.data?.error || 'Unknown error',
			error_text: 'An error occurred while fetching appointment types.',
			error_type: 'API error - offers',
			error_status: error?.response?.status === 403 ? 'Yes' : 'No',
		});
	}
};

/**
 * Main function to get appointment types by facility (coordinates or facility details)
 */
export const getTypesByFacility = async (
	latitude: string,
	longitude: string,
	siteId: string | undefined,
	params: AppointmentTypesParams
): Promise<void> => {
	const { facilityDetails } = params;

	// Handle case where lat/lon might be null/empty
	if (!latitude || !longitude) {
		if (!siteId || !facilityDetails) {
			devLog('Missing required parameters for facility-based initialization:', { siteId, facilityDetails });
			return;
		}
		devLog('Using facility-based appointment types loading');
		return await getAppointmentTypesByFacility(facilityDetails, siteId, params);
	}

	// Use coordinates-based approach
	return await getAppointmentTypesByCoordinates(latitude, longitude, params);
};

// Create case-insensitive lookup
export const normalizeApptType = (type: string, typeMapping: Record<string, string>): string | null => {
	const upperType = type.toUpperCase();

	// Map input variations to the exact keys used in allowedApptTypes
	return typeMapping[upperType] || null;
};

/**
 * Resolves an `appt-type` URL param to its internal appointment type, or null when the param is
 * missing, malformed, or not a reason we can schedule.
 *
 * The results page and SearchResults both gate on this same param, so they share this one resolver
 * against `typeMapping`: whatever this resolves is routable, whatever it doesn't sends the user home.
 * Keeping them on separate maps is what let "Workplace screening or exam" pass one check and fail
 * the other, bouncing the user back to the landing page.
 */
export const resolveApptTypeParam = (apptTypeParam: unknown): string | null => {
	if (typeof apptTypeParam !== 'string') {
		return null;
	}

	let decoded = apptTypeParam;
	try {
		decoded = decodeURIComponent(apptTypeParam);
	} catch {
		// A malformed escape sequence is matched as-is rather than throwing
	}

	const normalized = decoded.trim().replace(/\s+/g, ' ').toUpperCase();

	// `typeMapping` keys use both spaces ("SEXUAL HEALTH") and hyphens ("OM-COMPLEX"), so try the
	// param as written before swapping its separators. Matching first is what keeps OM-COMPLEX from
	// being normalized to "OM COMPLEX" and never resolving, while the swaps keep hand-written URLs
	// such as `sexual-health` working.
	const candidates = [normalized, normalized.replace(/[-_]+/g, ' '), normalized.replace(/[\s_]+/g, '-')];

	for (const candidate of candidates) {
		const resolved = normalizeApptType(candidate, typeMapping);
		if (resolved) {
			return resolved;
		}
	}

	return null;
};

// Revspring returns appt-type keys in whatever casing that clinic's config uses (e.g. "Illness"),
// so callers must never do a direct `appointmentTypes[apptType]`/`apptType in appointmentTypes` lookup.
export const hasApptType = (appointmentTypes: Record<string, unknown> | null | undefined, apptType: string): boolean =>
	Object.entries(appointmentTypes || {}).some(
		([key, value]) => key.toUpperCase() === apptType.toUpperCase() && Boolean(value)
	);
